import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import './AdminLogin.css';

// Optional client-side email whitelist (set REACT_APP_ADMIN_EMAILS as comma-separated list)
const ADMIN_EMAILS = (process.env.REACT_APP_ADMIN_EMAILS || '')
  .split(',')
  .map(e => e.trim().toLowerCase())
  .filter(Boolean);

const ShieldIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2L3 7V12C3 16.55 7.08 20.74 12 22C16.92 20.74 21 16.55 21 12V7L12 2Z"
      fill="rgba(239,68,68,0.15)" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9 12L11 14L15 10" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" strokeWidth="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <circle cx="12" cy="16" r="1.5" fill="currentColor" />
  </svg>
);

export default function AdminLogin() {
  const [form, setForm]     = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const { login, logout, user } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in as admin
  useEffect(() => {
    if (user?.role === 'admin') navigate('/admin', { replace: true });
  }, [user, navigate]);

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Client-side email whitelist check (UX guard only — backend enforces role)
    if (ADMIN_EMAILS.length && !ADMIN_EMAILS.includes(form.email.toLowerCase())) {
      toast.error('This email is not authorized for admin access');
      return;
    }

    setLoading(true);
    try {
      const data = await login(form.email, form.password);

      if (data?.user?.role !== 'admin') {
        // Logged in but not admin — clear session immediately
        logout();
        toast.error('Access denied — admin privileges required');
        return;
      }

      toast.success('Admin access granted. Welcome back.');
      navigate('/admin', { replace: true });
    } catch (err) {
      let msg;
      if (err.response?.status === 503) {
        msg = 'Server is starting up. Please wait and try again.';
      } else if (err.response?.data?.error) {
        msg = err.response.data.error;
      } else if (err.code === 'ERR_NETWORK' || !err.response) {
        msg = 'Cannot reach server — please wait 30 seconds and retry.';
      } else {
        msg = err.message || 'Authentication failed';
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-login-page">
      {/* Ambient background grid */}
      <div className="admin-login-bg" aria-hidden="true" />

      <div className="admin-login-card fade-in-up">

        {/* Top security badge */}
        <div className="admin-security-badge">
          <ShieldIcon />
          <div>
            <span className="admin-badge-label">RESTRICTED ACCESS</span>
            <span className="admin-badge-sub">Admin portal — AlphaVault</span>
          </div>
        </div>

        {/* Logo */}
        <Link to="/" className="admin-login-logo">▲ AlphaVault</Link>
        <h1 className="admin-login-title">Admin Sign In</h1>
        <p className="admin-login-sub">Authorized personnel only. All access is monitored and logged.</p>

        {/* Form */}
        <form onSubmit={handleSubmit} className="admin-login-form" autoComplete="on">
          <div className="input-group">
            <label className="input-label">Admin Email</label>
            <input
              name="email"
              type="email"
              required
              autoComplete="email"
              className="input-field admin-input"
              placeholder="admin@alphavault.io"
              value={form.email}
              onChange={handleChange}
              disabled={loading}
            />
          </div>

          <div className="input-group">
            <label className="input-label">Password</label>
            <div className="admin-pass-wrap">
              <input
                name="password"
                type={showPass ? 'text' : 'password'}
                required
                autoComplete="current-password"
                className="input-field admin-input"
                placeholder="••••••••••••"
                value={form.password}
                onChange={handleChange}
                disabled={loading}
              />
              <button
                type="button"
                className="pass-toggle"
                onClick={() => setShowPass(v => !v)}
                tabIndex={-1}
              >
                {showPass ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="btn admin-login-btn"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="admin-btn-spinner" />
                Authenticating…
              </>
            ) : (
              <>
                <LockIcon />
                Sign In to Admin Panel
              </>
            )}
          </button>
        </form>

        {/* Warning footer */}
        <div className="admin-login-warning">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
              stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Unauthorized access attempts are logged and may result in account suspension.
        </div>

        <div className="admin-login-back">
          <Link to="/">← Return to main site</Link>
        </div>
      </div>
    </div>
  );
}
